// =============================================================================
// net-journal.ts — durable record of "we tried to reach our server and didn't".
//
// WHY THIS EXISTS (the sank09 post-mortem, 2026-08-04 16:15–16:34 IST):
// A trader sat on the Connection Lost dialog for 19 minutes. Server-side we
// could see the hole — zero API requests, zero WS connects — but the device
// itself reported NOTHING to Kibana for the entire outage. Three separate
// reasons, all of which this module is built to close:
//
//   1. error-reporter's outbox is a FAILURE HANDLER, not a write-ahead log.
//      It parks a payload in localStorage from inside the report POST's
//      .then/.catch. When the origin blackholes, that POST hangs; when the
//      user then hits "Try Again" (window.location.reload), the document is
//      torn down and those callbacks never run. The event existed only in a
//      pending promise and died with the page.
//
//   2. `drainOutbox` treats a truthy `navigator.sendBeacon` as delivery and
//      clears the outbox. Beacon-returns-true only means "queued"; with the
//      origin unreachable the browser drops it later. Outbox emptied, nothing
//      delivered.
//
//   3. Dedup collapses the outage. The in-memory fingerprint window is 60 s
//      and the outbox key carries no time component, so 19 minutes of failures
//      compress to at most one row — with no attempt count and no duration.
//
// The fix is an inversion: **persist first, transmit later**. Every failed
// attempt is written to localStorage SYNCHRONOUSLY, before any network I/O is
// attempted, so persistence can never depend on the thing that just failed.
// Nothing is transmitted until reachability is positively confirmed — not
// `navigator.onLine`, which is exactly the signal that lied throughout this
// incident, but a real request that reached our server and came back.
//
// On recovery the whole outage ships as ONE `connectivity_outage` document
// carrying start, end, duration, attempt count, which probe rung failed, WS
// close codes, and how it resolved — with the per-attempt trail attached. That
// single row is the answer to "what happened on his device", which today takes
// a server-side archaeology session to half-reconstruct.
//
// Budget: the hot path (successful request) touches localStorage only on the
// first success after a failure. Failures write ~110 bytes each. Hard caps
// below bound the store well under any browser quota.
// =============================================================================

import { captureMessage, drainOutboxNow } from "./error-reporter"

// ─── Storage ────────────────────────────────────────────────────────────────
const JOURNAL_KEY = "__net_attempts_v1"
const OUTAGE_KEY = "__net_outage_v1"

// Ring-buffer caps. 300 × ~110 B ≈ 33 KB; the byte cap is the real ceiling and
// trims from the head (oldest first) so the most recent — most diagnostic —
// attempts always survive.
const MAX_ENTRIES = 300
const MAX_BYTES = 48 * 1024

// Entries shipped with the outage summary. The rest are still counted in
// `attempts`, they just don't ride along — a 300-entry trail would blow past
// the flattened-field budget on the ES side for no extra diagnostic value.
const MAX_SHIPPED_ENTRIES = 120

// An outage open longer than this is presumed abandoned (device slept, tab
// backgrounded for hours). Ship it anyway, flagged, rather than letting it sit
// in localStorage forever and contaminate the next real one.
const MAX_OUTAGE_MS = 2 * 60 * 60 * 1000

// Probe timeout. Deliberately shorter than apiFetch's 15 s: a probe exists to
// answer "is it back yet", and a 15 s hang per attempt would make the recovery
// loop unusable.
const PROBE_TIMEOUT_MS = 3000

// Measure the edge rung on every Nth probe only. See probeReachability() —
// the API rung subsumes it (a /api/ping response has already come through
// nginx), so the edge probe is purely diagnostic and does not need to run on
// every tick. At the 10 s steady recovery interval this is one extra request
// roughly every 50 s instead of one every 10 s.
const EDGE_PROBE_EVERY_N = 5
// Probe counter driving that cadence. Reset per outage so every incident
// measures the edge rung on its first probe, when the answer matters most.
let probeSeq = 0

export type JournalKind =
  | "fail" // an API call failed to reach the server
  | "ok" // first success after a failure — the recovery marker
  | "probe" // reachability probe result
  | "dialog" // Connection Lost dialog shown
  | "retry" // user pressed Try Again
  | "ws_close" // websocket closed
  | "ws_giveup" // websocket stopped retrying
  | "shell" // service-worker offline shell was displayed
  | "online" // navigator went online
  | "offline" // navigator went offline

/** Compact on-disk shape. Single-letter keys: this is stored per-attempt and
 *  the difference between `{"t":…,"k":…,"p":…}` and spelled-out keys is ~3× the
 *  bytes for identical information. */
export interface JournalEntry {
  /** epoch ms */
  t: number
  k: JournalKind
  /** path */
  p?: string
  /** method */
  m?: string
  /** error name (TypeError, TimeoutError, …) */
  e?: string
  /** elapsed ms */
  ms?: number
  /** navigator.onLine at the moment — 1/0 rather than bool to save bytes */
  on?: 0 | 1
  /** Network Information API effectiveType */
  et?: string
  /** request id, joins to the backend's X-Request-Id */
  rid?: string
  /** websocket close code */
  c?: number
  /** attempt / retry counter */
  n?: number
  /** free-form short label (probe rung, resolution reason) */
  d?: string
  /**
   * Occurrences, when this entry represents a consecutive run of identical
   * events. Absent means exactly one. See `appendJournal` — without this, a
   * long outage fills the whole ring buffer with identical probe failures and
   * evicts the actually-diagnostic entries.
   */
  r?: number
  /** Timestamp of the LAST occurrence in the run. `t` stays the first. */
  lt?: number
}

/**
 * Two entries describe the same event, differing only in when they happened.
 * Compared field-by-field rather than by serialising: this runs on every
 * append, including inside a failure burst.
 */
function sameShape(a: JournalEntry, b: JournalEntry): boolean {
  return a.k === b.k && a.p === b.p && a.m === b.m && a.e === b.e && a.d === b.d && a.on === b.on
}

/** True occurrence count of a journal, counting coalesced runs. */
function occurrenceCount(entries: JournalEntry[]): number {
  let total = 0
  for (const e of entries) total += e.r || 1
  return total
}

interface OutageState {
  id: string
  started: number
  trigger: string
  on_start: 0 | 1
  et_start: string
  fails: number
  probe_fails: number
  first_path: string
  ws_closes: number
  ws_gave_up: 0 | 1
  dialogs: number
  retries: number
  shells: number
  reloads: number
  /** Last rung status seen. Drives the derived-edge carry-forward in
   *  probeReachability; NOT what gets shipped — see the sticky flags below. */
  edge_ok: 0 | 1
  api_ok: 0 | 1
  /**
   * Sticky "this rung failed at least once during this outage".
   *
   * The shipped probe_edge_ok / probe_api_ok facets are derived from these, not
   * from edge_ok/api_ok. An outage that resolves via probeReachability() does so
   * on a SUCCESSFUL probe, and that probe writes edge_ok=1/api_ok=1 moments
   * before resolveOutage() reads them — so the last-seen values described every
   * probe-resolved outage as "both rungs fine", the exact opposite of the
   * diagnostic they exist for. A device that was offline for the whole incident
   * shipped edge_ok=true/api_ok=true while its own trail showed nothing but
   * failures. Sticky-on-failure is what "where was it broken" actually means.
   */
  edge_fail_seen: 0 | 1
  api_fail_seen: 0 | 1
}

// ─── Safe storage helpers ───────────────────────────────────────────────────
// Every accessor is total: Safari private mode throws on setItem, some Android
// webviews throw on getItem when storage is disabled by policy. This module
// runs on the error path — it must never be the thing that throws.

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn()
  } catch {
    return fallback
  }
}

function hasStorage(): boolean {
  return typeof window !== "undefined" && !!safe(() => window.localStorage, null)
}

function readJournal(): JournalEntry[] {
  if (!hasStorage()) return []
  return safe(() => {
    const raw = window.localStorage.getItem(JOURNAL_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as JournalEntry[]) : []
  }, [])
}

function writeJournal(entries: JournalEntry[]): void {
  if (!hasStorage()) return
  safe(() => {
    if (entries.length === 0) {
      window.localStorage.removeItem(JOURNAL_KEY)
      return
    }
    window.localStorage.setItem(JOURNAL_KEY, JSON.stringify(entries))
  }, undefined)
}

// In-memory mirror of the outage record.
//
// `noteRequestSuccess` runs on EVERY successful API call, and localStorage
// reads are synchronous main-thread work. Without this cache a healthy trader
// session would pay a `getItem` on every single request for the sole purpose
// of learning "no, there is still no outage" — the answer 99.9% of the time.
// With it, the hot path is one identity comparison and an early return, and
// storage is touched only when something has actually gone wrong.
//
// Safe because this module is the only writer within a document. The offline
// shell writes the same key, but that is a separate page load with its own
// fresh module state, so there is no staleness window to worry about.
// `undefined` means "not yet read from storage"; `null` means "read, none".
let outageCache: OutageState | null | undefined = undefined

function readOutage(): OutageState | null {
  if (outageCache !== undefined) return outageCache
  if (!hasStorage()) {
    outageCache = null
    return null
  }
  outageCache = safe(() => {
    const raw = window.localStorage.getItem(OUTAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === "object" ? (parsed as OutageState) : null
  }, null)
  return outageCache
}

function writeOutage(state: OutageState | null): void {
  outageCache = state
  if (!hasStorage()) return
  safe(() => {
    if (!state) {
      window.localStorage.removeItem(OUTAGE_KEY)
      return
    }
    window.localStorage.setItem(OUTAGE_KEY, JSON.stringify(state))
  }, undefined)
}

/** Test-only: drop the module-scoped state so a suite can simulate a fresh
 *  page load against pre-seeded localStorage. */
export function __resetOutageCacheForTests(): void {
  outageCache = undefined
  probeSeq = 0
}

// ─── Environment snapshots ──────────────────────────────────────────────────

function onlineFlag(): 0 | 1 {
  if (typeof navigator === "undefined") return 1
  return typeof navigator.onLine === "boolean" && !navigator.onLine ? 0 : 1
}

function effectiveType(): string {
  if (typeof navigator === "undefined") return ""
  const conn = (navigator as unknown as { connection?: { effectiveType?: string } }).connection
  return conn && typeof conn.effectiveType === "string" ? conn.effectiveType : ""
}

/** Strip the query string — it can carry tokens/OTPs and this trail is shipped
 *  to ELK. Mirrors `_crumbPath` in lib/api.ts. */
function cleanPath(path: string): string {
  if (!path) return ""
  const q = path.indexOf("?")
  const base = q === -1 ? path : path.slice(0, q)
  return base.slice(0, 120)
}

function newOutageId(): string {
  // Not security-sensitive — this only has to be unique enough to group the
  // rows belonging to one outage. crypto.randomUUID isn't universally present
  // on the older Android webviews in this user base.
  return `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`
}

// ─── Journal append ─────────────────────────────────────────────────────────

/**
 * Append one entry. Synchronous and total — this is the durability guarantee
 * the whole module rests on, so it does its own trimming rather than deferring
 * to any async cleanup.
 */
export function appendJournal(entry: JournalEntry): void {
  if (!hasStorage()) return
  const entries = readJournal()

  // ── Coalesce consecutive identical events ───────────────────────────────
  // A trader stuck with no connectivity keeps the recovery loop probing
  // indefinitely — one entry per probe, ~12–15 per minute. Appending each one
  // would fill the entire 300-entry buffer with identical "probe failed" rows
  // inside ~20 minutes and evict every entry that actually explains the
  // outage: the original API failures with their paths, error names and
  // timings. The run collapses to a single row carrying first-seen, last-seen
  // and a count, so storage stays flat no matter how long the outage lasts and
  // the diagnostic entries survive.
  const last = entries.length > 0 ? entries[entries.length - 1] : null
  if (last && sameShape(last, entry)) {
    last.r = (last.r || 1) + 1
    last.lt = entry.t
    if (!hasStorage()) return
    const s = safe(() => JSON.stringify(entries), "")
    if (s) safe(() => window.localStorage.setItem(JOURNAL_KEY, s), undefined)
    return
  }

  entries.push(entry)
  while (entries.length > MAX_ENTRIES) entries.shift()
  // Byte cap: serialise and trim from the head until it fits, rather than
  // estimating per-entry sizes (paths vary a lot in length). The result is
  // written directly instead of going through writeJournal(), which would
  // stringify the same array a second time — this runs once per failed
  // request, and during a burst that doubling is the only cost that scales.
  let serialized = safe(() => JSON.stringify(entries), "[]")
  while (serialized.length > MAX_BYTES && entries.length > 1) {
    entries.shift()
    serialized = safe(() => JSON.stringify(entries), "[]")
  }
  if (!hasStorage()) return
  safe(() => window.localStorage.setItem(JOURNAL_KEY, serialized), undefined)
}

// ─── Outage lifecycle ───────────────────────────────────────────────────────

// How much journal history predating an outage is worth keeping when one opens.
//
// The recorders below (probe / ws_close / online / offline) append
// unconditionally, because the events immediately BEFORE an outage — the
// `offline` that preceded the dialog, the socket close that preceded the first
// failed request — are part of the story. But the journal is only ever cleared
// in resolveOutage(), so on a healthy session those entries accumulated
// indefinitely and were then shipped attached to whatever outage happened next.
// Observed in ELK: a 2-second outage carrying a trail that started 15 hours
// earlier, with entries_total=23 describing events from a different day.
//
// Trimming at outage start keeps the useful lead-in and drops the orphans.
const PRE_HISTORY_MS = 60 * 1000

function beginOutage(trigger: string, firstPath: string): OutageState {
  // Drop pre-existing entries too old to belong to the incident now starting.
  // `lt` (last occurrence of a coalesced run) wins over `t` so a run that was
  // still firing seconds ago is kept even if it started long before.
  const existing = readJournal()
  if (existing.length > 0) {
    const cutoff = Date.now() - PRE_HISTORY_MS
    const fresh = existing.filter((e) => (e.lt || e.t) >= cutoff)
    if (fresh.length !== existing.length) writeJournal(fresh)
  }

  // Measure the edge rung on the first probe of THIS incident. The counter is
  // otherwise only reset at resolution, so a probe fired during the pre-dialog
  // grace window could consume tick 0 before the outage existed — leaving a
  // short outage with no in-window edge measurement at all, and a
  // probe_edge_ok that defaulted to "fine" for a device that was fully offline.
  probeSeq = 0

  const state: OutageState = {
    id: newOutageId(),
    started: Date.now(),
    trigger,
    on_start: onlineFlag(),
    et_start: effectiveType(),
    fails: 0,
    probe_fails: 0,
    first_path: cleanPath(firstPath),
    ws_closes: 0,
    ws_gave_up: 0,
    dialogs: 0,
    retries: 0,
    shells: 0,
    reloads: 0,
    edge_ok: 0,
    api_ok: 0,
    edge_fail_seen: 0,
    api_fail_seen: 0,
  }
  writeOutage(state)
  return state
}

/** Read the open outage, starting one if none exists. */
function ensureOutage(trigger: string, firstPath: string): OutageState {
  const existing = readOutage()
  if (existing) return existing
  return beginOutage(trigger, firstPath)
}

export function isOutageOpen(): boolean {
  return readOutage() !== null
}

// ─── Public recorders ───────────────────────────────────────────────────────

/**
 * A request never reached the server (transport error or our own 15 s
 * AbortSignal.timeout firing). Opens an outage if one isn't already open.
 *
 * Called from lib/api.ts's catch block, which has already computed everything
 * we need — this costs one localStorage write on a path that was failing
 * anyway.
 */
export function noteRequestFailure(opts: {
  path: string
  method: string
  errName: string
  ms: number
  requestId?: string
}): void {
  if (!hasStorage()) return
  const state = ensureOutage("fetch", opts.path)
  state.fails += 1
  // Backfill the path when the outage was opened by something that had none to
  // give — a dialog, a socket give-up, the offline shell. ensureOutage only
  // seeds first_path at creation, so those outages shipped
  // outage_first_failed_path empty even when requests demonstrably failed,
  // losing the single field that says what the trader was trying to do.
  if (!state.first_path) state.first_path = cleanPath(opts.path)
  writeOutage(state)
  appendJournal({
    t: Date.now(),
    k: "fail",
    p: cleanPath(opts.path),
    m: opts.method,
    e: opts.errName,
    ms: Math.round(opts.ms),
    on: onlineFlag(),
    et: effectiveType(),
    rid: opts.requestId,
  })
}

/**
 * A request completed — the server answered. This is the ONLY thing that
 * proves connectivity is genuinely back, which is why recovery hangs off it
 * rather than off the `online` event (which never fired for sank09 because
 * `navigator.onLine` never went false in the first place).
 *
 * Hot path: returns after one cheap localStorage read when no outage is open,
 * which is the overwhelmingly common case.
 */
export function noteRequestSuccess(opts: { path: string; ms: number }): void {
  if (!hasStorage()) return
  const state = readOutage()
  if (!state) return // no outage in flight — nothing to do
  appendJournal({
    t: Date.now(),
    k: "ok",
    p: cleanPath(opts.path),
    ms: Math.round(opts.ms),
    on: onlineFlag(),
    et: effectiveType(),
  })
  resolveOutage("request_succeeded", state)
}

/** Update a rung facet on the open outage WITHOUT writing a journal entry.
 *  Used for the edge rung when its value is derived rather than measured —
 *  recording a derived value as though we had probed it would be a lie in the
 *  trail. */
function setProbeFacet(rung: "edge" | "api", ok: boolean): void {
  const state = readOutage()
  if (!state) return
  if (rung === "edge") state.edge_ok = ok ? 1 : 0
  else state.api_ok = ok ? 1 : 0
  writeOutage(state)
}

export function noteProbe(rung: "edge" | "api", ok: boolean, ms: number): void {
  if (!hasStorage()) return
  const state = readOutage()
  if (state) {
    if (!ok) state.probe_fails += 1
    if (rung === "edge") {
      state.edge_ok = ok ? 1 : 0
      if (!ok) state.edge_fail_seen = 1
    }
    if (rung === "api") {
      state.api_ok = ok ? 1 : 0
      if (!ok) state.api_fail_seen = 1
    }
    writeOutage(state)
  }
  appendJournal({
    t: Date.now(),
    k: "probe",
    d: `${rung}:${ok ? "ok" : "fail"}`,
    ms: Math.round(ms),
    on: onlineFlag(),
  })
}

export function noteDialogShown(): void {
  if (!hasStorage()) return
  const state = ensureOutage("dialog", "")
  state.dialogs += 1
  writeOutage(state)
  appendJournal({ t: Date.now(), k: "dialog", on: onlineFlag(), et: effectiveType() })
}

export function noteRetryClicked(): void {
  if (!hasStorage()) return
  const state = readOutage()
  if (state) {
    state.retries += 1
    writeOutage(state)
  }
  appendJournal({ t: Date.now(), k: "retry", on: onlineFlag() })
}

export function noteWsClose(code: number, retryCount: number): void {
  if (!hasStorage()) return
  // A WS close alone does NOT open an outage. Sockets close for benign reasons
  // all day (205 times for sank09) and most recover on the next attempt within
  // a second. Only record against an outage that some *request* failure has
  // already opened — otherwise this would manufacture an outage row every time
  // a phone changed cell tower.
  const state = readOutage()
  if (state) {
    state.ws_closes += 1
    writeOutage(state)
  }
  appendJournal({ t: Date.now(), k: "ws_close", c: code, n: retryCount, on: onlineFlag() })
}

export function noteWsGaveUp(retryCount: number): void {
  if (!hasStorage()) return
  const state = ensureOutage("ws", "")
  state.ws_gave_up = 1
  state.ws_closes += 1
  writeOutage(state)
  appendJournal({ t: Date.now(), k: "ws_giveup", n: retryCount, on: onlineFlag() })
}

/** Called from the service-worker offline shell (public/trade/offline.html)
 *  via its own inline copy of this write — exported here so the page-side code
 *  and the shell agree on the schema. */
export function noteOfflineShell(): void {
  if (!hasStorage()) return
  const state = ensureOutage("shell", "")
  state.shells += 1
  writeOutage(state)
  appendJournal({ t: Date.now(), k: "shell", on: onlineFlag() })
}

export function noteOnlineEvent(): void {
  if (!hasStorage()) return
  appendJournal({ t: Date.now(), k: "online", on: 1, et: effectiveType() })
}

export function noteOfflineEvent(): void {
  if (!hasStorage()) return
  appendJournal({ t: Date.now(), k: "offline", on: 0 })
}

// ─── Reachability ladder ────────────────────────────────────────────────────
//
// `navigator.onLine` is only trustworthy when it says FALSE. When it says true
// it means "this device has a link", which is a completely different claim to
// "our server is reachable". Throughout the sank09 outage it read `online` on
// a 4g connection while not one request reached us.
//
// Two rungs, probed in parallel so a dead network costs one timeout, not two:
//
//   edge — HEAD /manifest.webmanifest. Same-origin, sub-1 KB, deliberately NOT
//          in the service-worker precache, so a 200 is a real network hit and
//          not a cached response masking a still-down server. Proves TCP + TLS
//          + nginx answered.
//   api  — GET /api/ping. Proves a request reached Express and came back, which
//          is the claim that actually matters to a trader.
//
// "Answered" is any status < 500, NOT just 2xx. That matters for rollout: a
// backend that predates /api/ping returns 404, and a 404 still proves the
// request reached the server. Without that rule a frontend deployed ahead of
// the backend would decide the network was down and strand users on the very
// screen this work exists to eliminate.

export interface ProbeResult {
  edge: boolean
  api: boolean
  /** True when the server demonstrably answered us. This is what "really
   *  online" means — not navigator.onLine. */
  reachable: boolean
  ms: number
}

function apiBase(): string {
  const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || ""
  return backendUrl ? backendUrl.replace(/\/$/, "") : ""
}

async function probeOne(url: string, method: "GET" | "HEAD"): Promise<boolean> {
  try {
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null
    const timer = setTimeout(() => ctrl?.abort(), PROBE_TIMEOUT_MS)
    try {
      const res = await fetch(url, {
        method,
        cache: "no-store",
        credentials: "omit",
        signal: ctrl?.signal,
      })
      return res.status >= 200 && res.status < 500
    } finally {
      clearTimeout(timer)
    }
  } catch {
    return false
  }
}

/**
 * Probe both rungs and record the result against any open outage. Returns
 * `reachable: true` only when the API rung answered — an edge-only success
 * means nginx is up but the app tier is wedged, which is not a state a trader
 * can do anything in.
 *
 * A successful API rung RESOLVES an open outage. This is deliberately the
 * earliest honest moment: a real response from our server, not
 * `navigator.onLine` flipping (which on Android is true throughout the exact
 * failure this module exists for) and not the `online` event. Waiting for the
 * app's next API call instead would leave the outage — and its journal —
 * unsent for as long as the trader happens to sit on a quiet screen.
 */
export async function probeReachability(): Promise<ProbeResult> {
  const started = Date.now()

  // ── One request per tick, not two ───────────────────────────────────────
  // The API rung alone decides recovery, and it subsumes the edge rung: a
  // response from /api/ping has already traversed nginx, so an edge probe
  // alongside it measures something we can derive for free. Firing both on
  // every tick doubled the request count on a radio we already know is
  // struggling, for no added information.
  //
  // The edge rung only carries information when the API rung FAILS — it is
  // what separates "this device can't reach us at all" from "nginx answered
  // but the app tier is wedged". That distinction is worth one request
  // occasionally, not one every tick, so it is measured on a slow cadence and
  // carried forward in between. Issued in parallel rather than sequentially so
  // a diagnostic tick never doubles the wall-clock latency.
  const measureEdge = probeSeq % EDGE_PROBE_EVERY_N === 0
  probeSeq++

  const [api, measuredEdge] = await Promise.all([
    probeOne(`${apiBase()}/api/ping`, "GET"),
    measureEdge ? probeOne("/manifest.webmanifest", "HEAD") : Promise.resolve(null),
  ])
  const ms = Date.now() - started

  // Record the rung results BEFORE resolving, so the outage document carries
  // the probe_edge_ok / probe_api_ok facets that say where it was broken.
  if (measuredEdge !== null) {
    noteProbe("edge", measuredEdge, ms)
  } else if (api) {
    setProbeFacet("edge", true) // derived, not measured — no journal entry
  }
  noteProbe("api", api, ms)

  const edge = measuredEdge !== null ? measuredEdge : api || readOutage()?.edge_ok === 1
  if (api && readOutage()) resolveOutage("probe_confirmed")
  return { edge, api, reachable: api, ms }
}

// ─── Outage resolution → the one document that explains the incident ────────

function resolveOutage(resolvedBy: string, known?: OutageState): void {
  const state = known || readOutage()
  if (!state) return

  const entries = readJournal()
  const ended = Date.now()
  const durationMs = Math.max(0, ended - state.started)

  const shipped = entries.slice(-MAX_SHIPPED_ENTRIES)

  // Release the backlog FIRST, before emitting this incident's summary.
  //
  // Ordering matters: the summary below is flushed eagerly (`urgent`), which
  // write-aheads it into the outbox and POSTs it in the same tick. Draining
  // afterwards would find that entry still parked — the POST has not settled
  // yet — and send a second copy. There is no server-side dedup on ingest (the
  // `fingerprint` field feeds ElastAlert grouping, not idempotency), so that
  // second copy lands as a duplicate document and double-counts the incident.
  //
  // Draining here also reads better: everything stranded during the outage
  // goes out, then the summary that explains it.
  drainOutboxNow()

  // Hand the incident off BEFORE dropping the durable copy, and clear in a
  // `finally` so an outage still can't wedge open.
  //
  // This used to clear first, on the reasoning that a throwing captureMessage
  // must not strand the outage. But that traded a hang we don't have (it
  // swallows everything internally) for silent, permanent loss: between the
  // clear and a successful POST the incident existed only in memory, and
  // anything that dropped it there — the reporter not yet initialised, a torn
  // down document — destroyed it outright with the durable copy already gone.
  // Measured on this build: every outage that spanned a page reload was lost.
  //
  // `urgent` skips the 3 s scheduled flush that `warn` normally rides, so the
  // reporter's write-ahead outbox holds a durable copy within milliseconds
  // rather than leaving a multi-second window where nothing on disk knows about
  // this incident.
  try {
    captureMessage(
      `connectivity_outage: ${Math.round(durationMs / 1000)}s unreachable, ` +
        `${state.fails} failed request(s), resolved_by=${resolvedBy}`,
      "warn",
      {
        component: "connectivity_outage",
        routeTag: "connectivity_outage",
        // Bypasses the reporter's session-expiry halt: an outage summary is one
        // document per incident, and an outage that ENDED in a logout is exactly
        // the case we most need to see.
        bypassHalt: true,
        // Every outage resolves from this one callsite with this one component,
        // so the reporter's 60 s fingerprint dedup would drop the second of two
        // outages inside a minute — the flapping-connection case, where the
        // journal has already been cleared and the trail would be lost with it.
        skipDedup: true,
        // One document per incident: get it onto disk now rather than letting it
        // sit in the in-memory queue for the 3 s `warn` flush interval.
        urgent: true,
        outage: {
          outage_id: state.id,
          started_at: state.started,
          ended_at: ended,
          duration_ms: durationMs,
          trigger: state.trigger,
          resolved_by: resolvedBy,
          attempts: state.fails,
          probe_failures: state.probe_fails,
          first_failed_path: state.first_path,
          ws_closes: state.ws_closes,
          ws_gave_up: state.ws_gave_up === 1,
          dialogs_shown: state.dialogs,
          retries_clicked: state.retries,
          offline_shells: state.shells,
          reloads: state.reloads,
          online_at_start: state.on_start === 1,
          online_at_end: onlineFlag() === 1,
          effective_type_start: state.et_start,
          effective_type_end: effectiveType(),
          // Derived from the sticky fail flags, NOT from the last-seen values:
          // the probe that resolves an outage is by definition a successful one,
          // so last-seen always read "fine" and inverted the diagnostic. False
          // here means "this rung failed at some point during the incident".
          probe_edge_ok: state.edge_fail_seen !== 1,
          probe_api_ok: state.api_fail_seen !== 1,
          // True number of events, counting coalesced runs — so "41 probe
          // failures" reads honestly even though they occupy one row.
          entries_total: occurrenceCount(entries),
          entries_rows: entries.length,
          entries_shipped: shipped.length,
        },
        netJournal: shipped,
      }
    )
  } finally {
    // Always clear, even if the capture path threw — an outage left open would
    // silently suppress every later one.
    writeOutage(null)
    writeJournal([])
    // Next incident measures the edge rung on its very first probe.
    probeSeq = 0
  }

  // NOTE: the outbox drain that used to sit here now runs BEFORE captureMessage
  // above — see the comment there. Draining after the eagerly-flushed summary
  // re-sent it, because its write-ahead copy is still parked while its POST is
  // in flight, and nothing server-side dedups on ingest.
  //
  // The drain itself remains essential and is the trigger that was missing: the
  // reporter's own drains hang off `pagehide`, `visibilitychange`, init and the
  // `online` event — and in the failure mode this whole module exists for,
  // `online` NEVER FIRES, because navigator.onLine never went false to begin
  // with. Confirmed reachability is the honest signal, and we hold it here.
}

/**
 * Force-resolve whatever is open. Used by the init path when a probe confirms
 * we're back after a reload, and by the staleness sweep.
 */
export function flushOutage(resolvedBy: string): void {
  resolveOutage(resolvedBy)
}

// ─── Init ───────────────────────────────────────────────────────────────────

let initialized = false

/**
 * Recover an outage that spanned a page load. This is the sank09 path exactly:
 * he pressed Try Again mid-outage, the reload destroyed every in-flight report,
 * and the service worker painted the offline shell. With the journal on disk,
 * the reload is just another entry — and once the app boots on a working
 * network the whole incident ships, including the part that happened before
 * the reload.
 *
 * Safe to call repeatedly; only the first call does anything.
 */
export function initNetJournal(): void {
  if (initialized) return
  if (!hasStorage()) return
  initialized = true

  const state = readOutage()
  if (!state) return

  // Abandoned outage (device slept, tab parked for hours). Ship it flagged
  // rather than letting it swallow the next real incident.
  if (Date.now() - state.started > MAX_OUTAGE_MS) {
    resolveOutage("stale", state)
    return
  }

  state.reloads += 1
  writeOutage(state)
  appendJournal({ t: Date.now(), k: "retry", d: "page_load", on: onlineFlag() })

  // Don't trust `navigator.onLine` here — prove it. If the app just booted, a
  // request already reached the server (the HTML itself), but the API tier is
  // a separate claim, so probe before declaring recovery.
  void probeReachability().then((r) => {
    if (r.reachable) resolveOutage("recovered_after_reload")
  })
}
